const PAYLOAD_KEYS = [
  'company_name',
  'first_name',
  'last_name',
  'position',
  'email',
  'phone_no',
  'website',
  'type_of_store',
  'full_address',
  'ein_business_license_number',
  'country',
  'additional_comments',
  'isSupportOpeningOrder',
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const URL_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(:\d+)?([/?#][^\s]*)?$/i;

const TEL_DISALLOWED_PATTERN = /[^\d\s-]/g;
const NON_DIGIT_PATTERN = /\D/g;
const DIGITS_ONLY_PATTERN = /^\d+$/;

function getMessages(formElement) {
  const data = formElement.dataset;

  return {
    required: data.klWholesaleMessageRequired || 'Please fill this in.',
    email: data.klWholesaleMessageEmail || 'Please enter a valid email address.',
    url: data.klWholesaleMessageUrl || 'Please enter a valid website address.',
    tel: data.klWholesaleMessageTel || 'Please enter a valid phone number.',
    number: data.klWholesaleMessageNumber || 'Please enter numbers only.',
    error: data.klWholesaleMessageError || 'Something went wrong. Please try again.',
    network: data.klWholesaleMessageNetwork || 'We could not reach the server.',
    success: data.klWholesaleMessageSuccess || 'Thank you.',
  };
}

function getDigits(value) {
  return String(value == null ? '' : value).replace(NON_DIGIT_PATTERN, '');
}

function getSanitisedTelValue(value) {
  const body = String(value == null ? '' : value).replace(TEL_DISALLOWED_PATTERN, '');

  return body ? '+' + body : '';
}

function findCountryByDialCode(countries, value, preferredCountry) {
  const digits = getDigits(value);

  let match = null;

  countries.forEach((country) => {
    if (!country.dialDigits || !digits.startsWith(country.dialDigits)) return;
    if (match && match.dialDigits.length >= country.dialDigits.length) return;

    match = country;
  });

  if (
    match &&
    preferredCountry &&
    preferredCountry.dialDigits.length === match.dialDigits.length &&
    digits.startsWith(preferredCountry.dialDigits)
  ) {
    return preferredCountry;
  }

  return match;
}

function parseTelValue(countries, value, preferredCountry) {
  const digits = getDigits(value);
  const country = findCountryByDialCode(countries, value, preferredCountry);
  const nationalDigits = country ? digits.slice(country.dialDigits.length) : '';

  return { digits: digits, country: country, nationalDigits: nationalDigits };
}

function formatTelValue(country, nationalDigits) {
  if (!country) return nationalDigits ? '+' + nationalDigits : '';

  return country.dialCode + ' ' + nationalDigits;
}

function getTelError(parsed, isRequired, messages) {
  if (!parsed.digits) return isRequired ? messages.required : '';
  if (!parsed.country) return messages.tel;
  if (!parsed.nationalDigits) return isRequired ? messages.required : '';

  const length = parsed.nationalDigits.length;

  return length >= parsed.country.minDigits && length <= parsed.country.maxDigits
    ? ''
    : messages.tel;
}

function getValueError(kind, value, isRequired, messages) {
  const trimmed = String(value == null ? '' : value).trim();

  if (!trimmed) return isRequired ? messages.required : '';

  switch (kind) {
    case 'email':
      return EMAIL_PATTERN.test(trimmed) ? '' : messages.email;
    case 'url':
      return URL_PATTERN.test(trimmed) ? '' : messages.url;
    case 'number':
      return DIGITS_ONLY_PATTERN.test(trimmed) ? '' : messages.number;
    default:
      return '';
  }
}

function normaliseValue(kind, value) {
  const trimmed = String(value == null ? '' : value).trim();

  if (kind !== 'url' || !trimmed) return trimmed;

  return /^https?:\/\//i.test(trimmed) ? trimmed : 'https://' + trimmed;
}

function buildPayload(entries) {
  const payload = {};

  PAYLOAD_KEYS.forEach((key) => {
    payload[key] = '';
  });

  entries.forEach((entry) => {
    payload[entry.key] = entry.value;
  });

  return payload;
}

function applySanitisedValue(controlElement, sanitise) {
  const currentValue = controlElement.value;
  const nextValue = sanitise(currentValue);

  if (nextValue === currentValue) return;

  const caretPosition = controlElement.selectionStart;

  controlElement.value = nextValue;

  if (caretPosition === null) return;

  try {
    const kept = sanitise(currentValue.slice(0, caretPosition)).length;

    controlElement.setSelectionRange(kept, kept);
  } catch (error) {
    console.debug('kl-wholesale-form: the caret could not be restored', { error: error });
  }
}

function readCountries(dialElement) {
  return Array.from(dialElement.options).map((option, optionIndex) => ({
    code: option.dataset.klWholesaleCountry || '',
    dialCode: option.value,
    dialDigits: getDigits(option.value),
    minDigits: Number.parseInt(option.dataset.klWholesaleMin, 10) || 4,
    maxDigits: Number.parseInt(option.dataset.klWholesaleMax, 10) || 15,
    optionIndex: optionIndex,
  }));
}

function getDefaultCountry(dialElement, countries) {
  const defaultIndex = Array.from(dialElement.options).findIndex(
    (option) => option.defaultSelected
  );

  return countries[defaultIndex === -1 ? 0 : defaultIndex] || null;
}

function setFlagCountry(flagElement, country) {
  const baseUrl = flagElement.dataset.klWholesaleFlagBase;

  if (!baseUrl || !country || !country.code) return;

  flagElement.src = baseUrl + country.code + '.svg';
}

function showCountry(telContext, country) {
  if (!country) return;

  telContext.country = country;
  telContext.dialElement.selectedIndex = country.optionIndex;
  setFlagCountry(telContext.flagElement, country);
}

function setCaretToEnd(controlElement) {
  try {
    const end = controlElement.value.length;

    controlElement.setSelectionRange(end, end);
  } catch (error) {
    console.debug('kl-wholesale-form: the phone caret could not be moved', { error: error });
  }
}

function readTelField(fieldElement) {
  const control = fieldElement.querySelector('[data-kl-wholesale-control]');
  const dialElement = fieldElement.querySelector('[data-kl-wholesale-dial]');
  const flagElement = fieldElement.querySelector('[data-kl-wholesale-flag]');

  if (!control || !dialElement || !flagElement) return null;

  const countries = readCountries(dialElement);

  if (!countries.length) return null;

  const defaultCountry = getDefaultCountry(dialElement, countries);

  return {
    control: control,
    dialElement: dialElement,
    flagElement: flagElement,
    countries: countries,
    defaultCountry: defaultCountry,
    country: defaultCountry,
  };
}

function handleTelInput(telContext) {
  const control = telContext.control;

  applySanitisedValue(control, getSanitisedTelValue);

  const parsed = parseTelValue(telContext.countries, control.value, telContext.country);

  if (parsed.country) showCountry(telContext, parsed.country);
}

function handleTelCountryChange(telContext) {
  const chosenCountry = telContext.countries[telContext.dialElement.selectedIndex];

  if (!chosenCountry) return;

  const parsed = parseTelValue(telContext.countries, telContext.control.value, telContext.country);

  telContext.country = chosenCountry;
  setFlagCountry(telContext.flagElement, chosenCountry);

  telContext.control.value = formatTelValue(chosenCountry, parsed.nationalDigits);
  telContext.control.focus();
  setCaretToEnd(telContext.control);
}

function handleTelFocus(telContext) {
  if (telContext.control.value) return;

  telContext.control.value = formatTelValue(telContext.country, '');
  setCaretToEnd(telContext.control);
}

function resetTelField(telContext) {
  showCountry(telContext, telContext.defaultCountry);
  telContext.control.value = formatTelValue(telContext.defaultCountry, '');
}

function getTelFieldValue(telContext) {
  const parsed = parseTelValue(telContext.countries, telContext.control.value, telContext.country);

  if (!parsed.nationalDigits) return '';

  return formatTelValue(parsed.country, parsed.nationalDigits);
}

function getFieldValue(fieldElement, telContexts) {
  const kind = fieldElement.dataset.klWholesaleKind;

  if (kind === 'radio') {
    const checked = fieldElement.querySelector('[data-kl-wholesale-control]:checked');

    return checked ? checked.dataset.klWholesaleChoice === 'yes' : '';
  }

  if (kind === 'tel') {
    const telContext = telContexts.get(fieldElement);

    return telContext ? getTelFieldValue(telContext) : '';
  }

  const control = fieldElement.querySelector('[data-kl-wholesale-control]');

  if (!control) return '';

  return normaliseValue(kind, control.value);
}

function setFieldError(fieldElement, message) {
  const errorElement = fieldElement.querySelector('[data-kl-wholesale-error]');
  const controls = Array.from(fieldElement.querySelectorAll('[data-kl-wholesale-control]'));

  fieldElement.classList.toggle('has-error', Boolean(message));

  if (errorElement) errorElement.textContent = message;

  controls.forEach((control) => {
    if (message) {
      control.setAttribute('aria-invalid', 'true');
      return;
    }

    control.removeAttribute('aria-invalid');
  });
}

function validateField(fieldElement, messages, telContexts) {
  const kind = fieldElement.dataset.klWholesaleKind;
  const isRequired = fieldElement.hasAttribute('data-kl-wholesale-required');

  if (kind === 'radio') {
    return isRequired && getFieldValue(fieldElement, telContexts) === '' ? messages.required : '';
  }

  if (kind === 'tel') {
    const telContext = telContexts.get(fieldElement);

    if (!telContext) return '';

    const parsed = parseTelValue(
      telContext.countries,
      telContext.control.value,
      telContext.country
    );

    return getTelError(parsed, isRequired, messages);
  }

  return getValueError(kind, getFieldValue(fieldElement, telContexts), isRequired, messages);
}

function setStatus(statusElement, message, state) {
  if (!statusElement) return;

  statusElement.textContent = message;
  statusElement.classList.toggle('is-error', state === 'error');
  statusElement.classList.toggle('is-success', state === 'success');
}

function initWholesaleForm(formElement) {
  const fields = Array.from(formElement.querySelectorAll('[data-kl-wholesale-field]'));
  const submitButton = formElement.querySelector('[data-kl-wholesale-submit]');
  const submitLabel = formElement.querySelector('[data-kl-wholesale-submit-label]');
  const resetButton = formElement.querySelector('[data-kl-wholesale-reset]');
  const statusElement = formElement.querySelector('[data-kl-wholesale-status]');
  const honeypot = formElement.querySelector('[data-kl-wholesale-honeypot]');
  const endpoint = formElement.dataset.klWholesaleEndpoint;

  if (!fields.length || !submitButton || !endpoint) return;

  const messages = getMessages(formElement);
  const idleLabel = submitLabel ? submitLabel.textContent : '';
  const busyLabel = formElement.dataset.klWholesaleLoadingLabel || idleLabel;
  const timeoutMs = (Number.parseInt(formElement.dataset.klWholesaleTimeout, 10) || 20) * 1000;
  const telContexts = new Map();

  let isSending = false;

  function setBusy(isBusy) {
    isSending = isBusy;
    submitButton.disabled = isBusy;
    formElement.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (submitLabel) submitLabel.textContent = isBusy ? busyLabel : idleLabel;
  }

  fields.forEach((fieldElement) => {
    if (fieldElement.dataset.klWholesaleKind !== 'tel') return;

    const telContext = readTelField(fieldElement);

    if (!telContext) return;

    telContexts.set(fieldElement, telContext);

    const parsed = parseTelValue(
      telContext.countries,
      telContext.control.value,
      telContext.defaultCountry
    );

    showCountry(telContext, parsed.country || telContext.defaultCountry);

    telContext.control.addEventListener('input', () => handleTelInput(telContext));
    telContext.control.addEventListener('focus', () => handleTelFocus(telContext));
    telContext.dialElement.addEventListener('change', () => {
      handleTelCountryChange(telContext);

      if (!fieldElement.classList.contains('has-error')) return;

      setFieldError(fieldElement, validateField(fieldElement, messages, telContexts));
    });
  });

  fields.forEach((fieldElement) => {
    if (fieldElement.dataset.klWholesaleKind !== 'number') return;

    const control = fieldElement.querySelector('[data-kl-wholesale-control]');

    if (!control) return;

    control.addEventListener('input', () => applySanitisedValue(control, getDigits));
  });

  fields.forEach((fieldElement) => {
    const controls = Array.from(fieldElement.querySelectorAll('[data-kl-wholesale-control]'));

    controls.forEach((control) => {
      control.addEventListener('blur', () => {
        setFieldError(fieldElement, validateField(fieldElement, messages, telContexts));
      });

      control.addEventListener('input', () => {
        if (!fieldElement.classList.contains('has-error')) return;

        setFieldError(fieldElement, validateField(fieldElement, messages, telContexts));
      });

      control.addEventListener('change', () => {
        if (!fieldElement.classList.contains('has-error')) return;

        setFieldError(fieldElement, validateField(fieldElement, messages, telContexts));
      });
    });
  });

  if (resetButton) {
    resetButton.addEventListener('click', () => {
      fields.forEach((fieldElement) => setFieldError(fieldElement, ''));
      setStatus(statusElement, '', 'idle');
    });
  }

  formElement.addEventListener('reset', () => {
    telContexts.forEach(resetTelField);
  });

  formElement.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (isSending) return;

    setStatus(statusElement, '', 'idle');

    let firstInvalidField = null;

    fields.forEach((fieldElement) => {
      const message = validateField(fieldElement, messages, telContexts);

      setFieldError(fieldElement, message);

      if (message && !firstInvalidField) firstInvalidField = fieldElement;
    });

    if (firstInvalidField) {
      const control = firstInvalidField.querySelector('[data-kl-wholesale-control]');

      if (control) control.focus();

      return;
    }

    if (honeypot && honeypot.value.trim()) {
      setStatus(statusElement, messages.success, 'success');
      formElement.reset();
      return;
    }

    const payload = buildPayload(
      fields.map((fieldElement) => ({
        key: fieldElement.dataset.klWholesaleField,
        value: getFieldValue(fieldElement, telContexts),
      }))
    );

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

    setBusy(true);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined,
      });

      if (!response.ok) {
        console.debug('kl-wholesale-form: the endpoint refused the application', {
          status: response.status,
        });
        setStatus(statusElement, messages.error, 'error');
        return;
      }

      setStatus(statusElement, messages.success, 'success');
      formElement.reset();
      fields.forEach((fieldElement) => setFieldError(fieldElement, ''));
    } catch (error) {
      console.debug('kl-wholesale-form: the application could not be sent', { error: error });
      setStatus(statusElement, messages.network, 'error');
    } finally {
      if (timer) clearTimeout(timer);
      setBusy(false);
    }
  });
}

document.querySelectorAll('[data-kl-wholesale-form]').forEach(initWholesaleForm);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-kl-wholesale-form]').forEach(initWholesaleForm);
});
